// Runtime a11y fix for Vuetify 2 VSelect / VAutocomplete / VCombobox.
//
// Vuetify 2 renders the `.v-input__slot` wrapper of a select with an interactive
// `role="button"` (plus aria-haspopup/aria-expanded/aria-owns) while ALSO rendering
// a focusable <input> inside it. An interactive control nested inside another
// interactive control fails WCAG 4.1.2 (axe rule: nested-interactive).
//
// There is no prop in Vuetify 2 to change this markup, so we patch it at runtime:
// move the combobox semantics onto the <input> that actually takes focus and
// neutralize the wrapper's interactive role. This is the standard ARIA combobox
// pattern, applied globally via a MutationObserver so it covers every select in
// the app (including ones mounted on demand inside dialogs / data tables) and stays
// in sync as Vuetify re-applies state on menu open/close.

const MIRRORED_ATTRS = ['aria-expanded', 'aria-owns', 'aria-activedescendant']
const SLOT_SELECTOR = '.v-select .v-input__slot'

function setOrRemove(el, attr, value) {
  // Only touch the DOM when the value actually differs, so the observer we run
  // does not loop on its own mutations.
  if (value === null || value === undefined) {
    if (el.hasAttribute(attr)) el.removeAttribute(attr)
  } else if (el.getAttribute(attr) !== value) {
    el.setAttribute(attr, value)
  }
}

function removeIfPresent(el, attr) {
  if (el.hasAttribute(attr)) el.removeAttribute(attr)
}

function patchSlot(slot) {
  if (!slot || !slot.closest || !slot.closest('.v-select')) return
  const input = slot.querySelector('input')
  if (!input) return

  // Combobox semantics belong on the focusable input.
  setOrRemove(input, 'role', 'combobox')
  setOrRemove(input, 'aria-haspopup', 'listbox')
  // Mirror live state from the slot ONLY when present. We strip these from the
  // slot below, which re-fires the observer; clearing the input on that pass
  // (when the slot no longer has the attr) would immediately undo the mirror.
  MIRRORED_ATTRS.forEach(attr => {
    const value = slot.getAttribute(attr)
    if (value !== null) setOrRemove(input, attr, value)
  })

  // Neutralize the wrapper so it is a plain, non-interactive div.
  removeIfPresent(slot, 'role')
  removeIfPresent(slot, 'aria-haspopup')
  MIRRORED_ATTRS.forEach(attr => removeIfPresent(slot, attr))
}

function scan(root) {
  if (!root || root.nodeType !== 1) return
  if (root.matches && root.matches(SLOT_SELECTOR)) patchSlot(root)
  if (root.querySelectorAll) root.querySelectorAll(SLOT_SELECTOR).forEach(patchSlot)
}

export function initSelectA11y() {
  if (typeof window === 'undefined' || !('MutationObserver' in window)) return

  scan(document.body)

  const observer = new MutationObserver(mutations => {
    for (const m of mutations) {
      if (m.type === 'childList') {
        m.addedNodes.forEach(scan)
      } else if (m.type === 'attributes') {
        const target = m.target
        if (target.matches && target.matches(SLOT_SELECTOR)) patchSlot(target)
      }
    }
  })

  observer.observe(document.body, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeFilter: [
      'role',
      'aria-haspopup',
      'aria-expanded',
      'aria-owns',
      'aria-activedescendant',
      'class',
    ],
  })
}
